import { Box, Button, Menu, MenuItem, Tooltip, SxProps, Theme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import { useRef, useState } from "react";

import { FilterComponent } from "./filter_component";
import { PerspectiveViewFilter } from "../../entities/perspective_view_filter";
import { CONTROL_LABEL_SX } from "../../utils/control_label";
import { formatColumnCaption } from "../../utils/mui_table_utils";

const toArray = (v: string[] | string | undefined): string[] =>
  Array.isArray(v) ? v : v != null && v !== "" ? [String(v)] : [];

export function FilterBy({
  isLoading,
  excludedFilterColumnFilter,
  viewFilters,
  setViewFilters,
  columns,
  label,
  sx,
}: {
  isLoading: boolean;
  excludedFilterColumnFilter: string[];
  viewFilters: PerspectiveViewFilter[];
  setViewFilters: (newFilters: PerspectiveViewFilter[]) => void;
  columns: {
    name: string;
    caption: string;
    unique_values: string[];
  }[];
  label?: string;
  sx?: SxProps<Theme>;
}) {
  const safeViewFilters = Array.isArray(viewFilters) ? viewFilters : [];
  const userFilters = safeViewFilters.filter(
    (filter) => !excludedFilterColumnFilter.includes(filter[0])
  );
  const systemFilters = safeViewFilters.filter((filter) =>
    excludedFilterColumnFilter.includes(filter[0])
  );

  // Live commit: rebuild the full filter list (system filters preserved) and
  // dispatch immediately through the existing setViewFilters path. No temp /
  // confirm step — the data model and how filters reach the query are unchanged.
  const commit = (nextUserFilters: PerspectiveViewFilter[]) => {
    setViewFilters([...systemFilters, ...nextUserFilters]);
  };

  const updateFilter = (index: number, next: PerspectiveViewFilter) => {
    const copy = [...userFilters];
    copy[index] = next;
    commit(copy);
  };

  // "Add filter" opens a field picker first — the card only appears once the
  // user has chosen which kind of filter to add, instead of defaulting to the
  // first column (usually SKU) and popping its value list. The value input is
  // deliberately NOT auto-focused after the choice: openOnFocus would pop the
  // value dropdown immediately, which reads like the old auto-open bug.
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  // Guard against the opening click's mouseup landing on a menu item (the
  // menu can be repositioned over the cursor near the viewport bottom): a
  // selection within 250ms of opening can't be an intentional second click.
  const addMenuOpenedAt = useRef(0);

  const openAddMenu = (e: React.MouseEvent<HTMLElement>) => {
    addMenuOpenedAt.current = Date.now();
    setAddAnchor(e.currentTarget);
  };

  const addFilter = (columnName: string) => {
    if (Date.now() - addMenuOpenedAt.current < 250) return;
    setAddAnchor(null);
    commit([...userFilters, [columnName, "in", []]]);
  };

  // Stable per-card keys: field+operator (plus an occurrence index for
  // duplicate field/operator pairs) instead of the array index, so removing or
  // reordering a filter doesn't remount its siblings and discard their
  // in-progress input state.
  const filterKeys = userFilters.map((f, i, arr) => {
    const sig = `${f[0]}::${f[1]}`;
    const dupIndex = arr
      .slice(0, i)
      .filter((g) => `${g[0]}::${g[1]}` === sig).length;
    return `${sig}::${dupIndex}`;
  });

  return (
    <Box sx={sx}>
      <Box
        sx={{
          ...CONTROL_LABEL_SX,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
          {label ?? "Filter by"}
          <Tooltip title="You can filter by multiple values in same filter by selecting them in the dropdown. Filters are additive, not conditional.">
            <InfoIcon
              fontSize="small"
              sx={{
                width: "16px",
                height: "16px",
                color: "rgba(0, 0, 0, 0.3)",
                ml: 0.5,
              }}
            />
          </Tooltip>
        </Box>
        {userFilters.length > 0 && (
          <Box
            component="span"
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: 0,
              textTransform: "none",
              color: (t) => t.palette.atomic.brandBlueDark,
            }}
          >
            {userFilters.length} active
          </Box>
        )}
      </Box>

      {!isLoading &&
        userFilters.map((f, i) => (
          <FilterComponent
            key={filterKeys[i]}
            filter={f}
            columns={columns}
            isLoading={isLoading}
            onChangeField={(col) => updateFilter(i, [col, f[1], f[2]])}
            onChangeOperator={(op) => updateFilter(i, [f[0], op, f[2]])}
            onAddValue={(v) => {
              const existing = toArray(f[2]);
              const t = v.trim();
              if (!t || existing.includes(t)) return;
              updateFilter(i, [f[0], f[1], [...existing, t]]);
            }}
            onAddValues={(vs) => {
              const merged = [...toArray(f[2])];
              vs.forEach((v) => {
                const t = v.trim();
                if (t && !merged.includes(t)) merged.push(t);
              });
              updateFilter(i, [f[0], f[1], merged]);
            }}
            onRemoveValue={(v) =>
              updateFilter(i, [f[0], f[1], toArray(f[2]).filter((x) => x !== v)])
            }
            onRemove={() => commit(userFilters.filter((_, idx) => idx !== i))}
          />
        ))}

      <Button
        fullWidth
        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        disabled={isLoading || columns.length === 0}
        onClick={openAddMenu}
        sx={{
          justifyContent: "center",
          border: "1px dashed",
          borderColor: (t) => t.palette.atomic.border,
          borderRadius: "6px",
          fontSize: "12.5px",
          color: (t) => t.palette.atomic.inkSecondary,
          padding: "6px 8px",
        }}
      >
        Add filter
      </Button>

      <Menu
        anchorEl={addAnchor}
        open={Boolean(addAnchor)}
        onClose={() => setAddAnchor(null)}
        // Drop the menu BELOW the button. With the default placement the
        // first item renders under the cursor, and the mouseup of the
        // opening click can click-through and instantly select it — which
        // then looked like the old "value list opens by itself" bug.
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        // Compact scrollable dropdown, not a viewport-tall overlay.
        slotProps={{ paper: { sx: { maxHeight: 280, minWidth: 200 } } }}
      >
        {[...columns]
          .sort((a, b) =>
            (a.caption || a.name).localeCompare(b.caption || b.name)
          )
          .map((c) => (
            <MenuItem
              key={c.name}
              onClick={() => addFilter(c.name)}
              sx={{ fontSize: 13 }}
            >
              {c.caption ?? formatColumnCaption(c.name)}
            </MenuItem>
          ))}
      </Menu>

      {userFilters.length > 1 && (
        <Box sx={{ textAlign: "center", mt: "6px" }}>
          <Button
            onClick={() => commit([])}
            sx={{
              fontSize: "11px",
              minWidth: 0,
              padding: "2px 6px",
              color: (t) => t.palette.atomic.muted,
            }}
          >
            Clear all
          </Button>
        </Box>
      )}
    </Box>
  );
}
